import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { UserPlus, Trash2, KeyRound, Loader2, AlertCircle, CheckCircle2, ShieldCheck } from "lucide-react";
import { supabase } from "../lib/supabase";

interface AdminUser {
  id: string;
  email: string;
  createdAt: string;
  lastSignInAt: string | null;
  isAdmin: boolean;
}

const fetchUsers = async (): Promise<AdminUser[]> => {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData.session?.access_token;
  if (!token) throw new Error("Not authenticated");
  const res = await fetch("/api/admin-users", {
    headers: { Authorization: `Bearer ${token}` },
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error ?? `HTTP ${res.status}`);
  return json.users as AdminUser[];
};

const apiCall = async (op: string, body: object): Promise<{ ok: boolean }> => {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData.session?.access_token;
  if (!token) throw new Error("Not authenticated");
  const url = op === "create" ? "/api/admin-users" : `/api/admin-users?op=${op}`;
  const res = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error ?? `HTTP ${res.status}`);
  return json;
};

export default function SettingsPage(): JSX.Element {
  const qc = useQueryClient();
  const { data: users = [], isLoading, error } = useQuery({
    queryKey: ["admin-users"],
    queryFn: fetchUsers,
  });

  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newName, setNewName] = useState("");
  const [feedback, setFeedback] = useState<{ kind: "ok" | "err"; msg: string } | null>(null);

  const createMut = useMutation({
    mutationFn: () => apiCall("create", { email: newEmail.trim(), password: newPassword, fullName: newName.trim() }),
    onSuccess: () => {
      setFeedback({ kind: "ok", msg: `Utilisateur ${newEmail} créé.` });
      setNewEmail("");
      setNewPassword("");
      setNewName("");
      void qc.invalidateQueries({ queryKey: ["admin-users"] });
    },
    onError: (e) => setFeedback({ kind: "err", msg: (e as Error).message }),
  });

  const deleteMut = useMutation({
    mutationFn: (userId: string) => apiCall("delete", { userId }),
    onSuccess: () => {
      setFeedback({ kind: "ok", msg: "Utilisateur supprimé." });
      void qc.invalidateQueries({ queryKey: ["admin-users"] });
    },
    onError: (e) => setFeedback({ kind: "err", msg: (e as Error).message }),
  });

  const resetMut = useMutation({
    mutationFn: ({ userId, password }: { userId: string; password: string }) =>
      apiCall("reset", { userId, password }),
    onSuccess: () => setFeedback({ kind: "ok", msg: "Mot de passe mis à jour." }),
    onError: (e) => setFeedback({ kind: "err", msg: (e as Error).message }),
  });

  const onSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    setFeedback(null);
    if (!newEmail.trim() || newPassword.length < 8) {
      setFeedback({ kind: "err", msg: "Email + mot de passe (≥8 caractères) requis." });
      return;
    }
    createMut.mutate();
  };

  const onDelete = (u: AdminUser): void => {
    if (u.isAdmin) {
      setFeedback({ kind: "err", msg: "Impossible de supprimer un admin." });
      return;
    }
    if (!window.confirm(`Supprimer définitivement ${u.email} ?`)) return;
    deleteMut.mutate(u.id);
  };

  const onReset = (u: AdminUser): void => {
    const password = window.prompt(`Nouveau mot de passe pour ${u.email} (≥8 caractères)`);
    if (!password || password.length < 8) {
      setFeedback({ kind: "err", msg: "Mot de passe trop court." });
      return;
    }
    resetMut.mutate({ userId: u.id, password });
  };

  return (
    <div className="space-y-8 mt-8">
      <header>
        <div className="mb-2 label-caps">Settings · Utilisateurs</div>
        <h1 className="title-dual text-h1">Gestion des accès</h1>
        <p className="mt-3 text-foreground/60 text-lead max-w-2xl">
          Crée des comptes pour ton équipe. Chaque utilisateur reçoit un email + mot de passe que tu lui partages
          en privé. Les admins peuvent gérer cette page; les autres peuvent juste indexer et chercher.
        </p>
      </header>

      <section className="card-koloc">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="label-caps mb-1">Ajouter un utilisateur</div>
            <h2 className="text-h3 font-semibold">Nouveau compte</h2>
          </div>
          <UserPlus className="h-4 w-4 text-foreground/40" />
        </div>
        <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-1">
            <label className="label-caps mb-2 block">Nom complet</label>
            <input
              className="input-koloc"
              placeholder="Prénom Nom"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              disabled={createMut.isPending}
            />
          </div>
          <div className="md:col-span-1">
            <label className="label-caps mb-2 block">Email <span className="text-foreground">·</span></label>
            <input
              className="input-koloc"
              type="email"
              required
              placeholder="prenom@koloc.studio"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              disabled={createMut.isPending}
            />
          </div>
          <div className="md:col-span-1">
            <label className="label-caps mb-2 block">Mot de passe <span className="text-foreground">·</span></label>
            <input
              className="input-koloc"
              type="text"
              required
              minLength={8}
              placeholder="≥ 8 caractères"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={createMut.isPending}
            />
          </div>
          <div className="md:col-span-3 flex justify-between items-center mt-2">
            <span className="text-caption text-foreground/40">
              Le mot de passe est visible ici (pour le partager). L'utilisateur peut le changer après.
            </span>
            <button type="submit" className="pill-solid" disabled={createMut.isPending}>
              {createMut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserPlus className="h-3.5 w-3.5" />}
              {createMut.isPending ? "Création…" : "Créer"}
            </button>
          </div>
        </form>
      </section>

      {feedback && (
        <div
          className={
            feedback.kind === "ok"
              ? "rounded-md border border-success/30 bg-success/5 p-4 text-caption text-success flex gap-3"
              : "rounded-md border border-destructive/30 bg-destructive/5 p-4 text-caption text-destructive flex gap-3"
          }
        >
          {feedback.kind === "ok" ? (
            <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" />
          ) : (
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
          )}
          <div>{feedback.msg}</div>
        </div>
      )}

      <section>
        <div className="mb-4 flex items-baseline justify-between">
          <div className="label-caps">
            {isLoading ? "Chargement…" : `${users.length} utilisateur${users.length > 1 ? "s" : ""}`}
          </div>
        </div>

        {error ? (
          <div className="card-koloc text-center py-12">
            <AlertCircle className="h-5 w-5 text-destructive mx-auto mb-3" />
            <div className="text-caption text-destructive">{(error as Error).message}</div>
            <p className="text-caption text-foreground/40 mt-2 max-w-md mx-auto">
              Vérifie que la fonction serverless <code>/api/admin-users</code> est déployée et que les env vars
              <code> SUPABASE_SERVICE_ROLE_KEY</code> et <code>ADMIN_EMAILS</code> sont configurées sur Vercel.
            </p>
          </div>
        ) : isLoading ? (
          <div className="overflow-hidden rounded-md border border-foreground/8 bg-surface">
            {[0, 1, 2].map((i) => (
              <div key={i} className="border-b border-foreground/8 last:border-0 p-4">
                <div className="shimmer h-4 w-1/3" />
                <div className="shimmer mt-2 h-3 w-1/4" />
              </div>
            ))}
          </div>
        ) : (
          <div className="overflow-hidden rounded-md border border-foreground/8 bg-surface">
            <table className="w-full">
              <thead>
                <tr className="border-b border-foreground/8">
                  <Th className="w-[42%]">Email</Th>
                  <Th>Rôle</Th>
                  <Th>Créé</Th>
                  <Th>Dernière connexion</Th>
                  <Th className="w-32 text-right">Actions</Th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-foreground/8 last:border-0 hover:bg-foreground/[0.025]">
                    <Td>
                      <div className="font-medium">{u.email}</div>
                    </Td>
                    <Td>
                      {u.isAdmin ? (
                        <span className="badge inline-flex items-center gap-1.5">
                          <ShieldCheck className="h-3 w-3" />
                          Admin
                        </span>
                      ) : (
                        <span className="badge-outline">Membre</span>
                      )}
                    </Td>
                    <Td className="text-foreground/60 num">{formatDate(u.createdAt)}</Td>
                    <Td className="text-foreground/60 num">
                      {u.lastSignInAt ? formatDate(u.lastSignInAt) : <span className="text-foreground/30">jamais</span>}
                    </Td>
                    <Td className="text-right">
                      <div className="inline-flex gap-1.5">
                        <button
                          onClick={() => onReset(u)}
                          className="grid h-7 w-7 place-items-center rounded-pill text-foreground/60 hover:text-foreground hover:bg-foreground/8"
                          title="Changer le mot de passe"
                          disabled={resetMut.isPending}
                        >
                          <KeyRound className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => onDelete(u)}
                          className="grid h-7 w-7 place-items-center rounded-pill text-foreground/60 hover:text-destructive hover:bg-destructive/8 disabled:opacity-30"
                          title="Supprimer"
                          disabled={u.isAdmin || deleteMut.isPending}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </Td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr>
                    <Td className="text-center py-8 text-foreground/50" colSpan={5}>
                      Aucun utilisateur — ajoute le premier avec le formulaire ci-dessus.
                    </Td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function Th({ children, className }: { children?: React.ReactNode; className?: string }): JSX.Element {
  return (
    <th className={["px-4 py-3 text-left label-caps text-foreground/50 font-medium", className].filter(Boolean).join(" ")}>
      {children}
    </th>
  );
}
function Td({
  children,
  className,
  colSpan,
}: {
  children?: React.ReactNode;
  className?: string;
  colSpan?: number;
}): JSX.Element {
  return (
    <td colSpan={colSpan} className={["px-4 py-3 text-body align-middle", className].filter(Boolean).join(" ")}>
      {children}
    </td>
  );
}
function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("fr-CA", { year: "numeric", month: "short", day: "numeric" });
}
